import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonItem,
  IonList,
  IonFooter,
  IonButton,
  IonIcon,
  IonSpinner,
  IonText,
  IonPopover,
  IonToggle,
  IonToast,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonAlert,
} from '@ionic/react';
import {
  ellipsisVertical,
  keyOutline,
  mic,
  pause,
  peopleOutline,
  play,
  send,
  square,
  trash,
  closeOutline,
  shareOutline,
} from 'ionicons/icons';
import { RouteComponentProps } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { apiUrl, apiHeaders } from '../lib/api';
import { useI18n } from '../i18n/I18nProvider';
import ShareLinkSheet from '../components/ShareLinkSheet';
import {
  buildChatDeepLink,
  buildDeepLinkShareText,
} from '../lib/deepLinks';
import {
  decryptAudioAny,
  decryptTextAny,
  encryptAudioV3,
  encryptTextV3,
  generateMessageDek,
  isEncryptedAudioContent,
  isEncryptedTextContent,
} from '../lib/chatCrypto';

type ComposerMode = 'plain' | 'ghost' | 'coded';

interface MessageAttachment {
  id: string;
  url: string;
  type: string;
  size: number;
  sizeBytes?: number | null;
  durationSec?: number | null;
  mimeType?: string | null;
}

interface MessageSender {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

interface Message {
  id: string;
  content: string | null;
  type: 'TEXT' | 'IMAGE' | 'FILE' | 'AUDIO';
  createdAt: string;
  senderId: string;
  sender: MessageSender | null;
  attachments: MessageAttachment[];
  replyToMessageId?: string | null;
  replyTo?: {
    id: string;
    unavailable?: boolean;
    content?: string | null;
    type?: 'TEXT' | 'IMAGE' | 'FILE' | 'AUDIO';
    senderId?: string;
    sender?: MessageSender | null;
    attachments?: MessageAttachment[];
    decodeKey?: string | null;
    isHidden?: boolean;
    revealedAt?: string | null;
    unlockedAt?: string | null;
    revealType?: 'MANUAL' | 'SCHEDULED' | 'CONDITIONAL';
    canReveal?: boolean;
  } | null;
  decodeKey?: string | null;
  isHidden?: boolean;
  revealedAt?: string | null;
  unlockedAt?: string | null;
  revealType?: 'MANUAL' | 'SCHEDULED' | 'CONDITIONAL';
  canReveal?: boolean;
}

type AvailabilityKind = 'unghost' | 'unlock';

interface AvailabilityActivity {
  id: string;
  kind: AvailabilityKind;
  messageId: string;
  at: string;
  actorLabel: string;
}

interface MessagesResponse {
  data: Message[];
  nextCursor: string | null;
}

interface KeysResponse {
  keys: string[];
}

interface ReadStateResponse {
  lastReadMessageId: string | null;
  lastReadAt: string | null;
  unreadCount: number;
}

interface ConversationMember {
  userId: string;
  role: string;
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
  };
}

interface ConversationMembersResponse {
  conversationName?: string;
  data: ConversationMember[];
}

interface ChatBootstrapResponse {
  messages: MessagesResponse;
  readState: ReadStateResponse;
  keys: KeysResponse;
  members: ConversationMembersResponse;
}

interface RouteParams {
  conversationId: string;
}

type Props = RouteComponentProps<RouteParams>;

const MAX_AUDIO_DURATION_SEC = 300;
const SUPPORTED_AUDIO_MIME_TYPES = ['audio/webm', 'audio/mp4', 'audio/mpeg'];

interface AudioPlaybackState {
  playing: boolean;
  currentTime: number;
  duration: number;
}

const formatAudioTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const toTimeMs = (value?: string | null): number => {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

/** When the message became newly readable (send, unghost, or coded reveal). */
const getMessageAvailableAt = (m: Pick<Message, 'createdAt' | 'revealedAt' | 'unlockedAt'>): string => {
  const created = toTimeMs(m.createdAt);
  const revealed = toTimeMs(m.revealedAt);
  const unlocked = toTimeMs(m.unlockedAt);
  const max = Math.max(created, revealed, unlocked);
  if (unlocked === max && m.unlockedAt) return m.unlockedAt;
  if (revealed === max && m.revealedAt) return m.revealedAt;
  return m.createdAt;
};

const upsertMessageByCreatedAt = (list: Message[], incoming: Message): Message[] => {
  const idx = list.findIndex((m) => m.id === incoming.id);
  if (idx >= 0) {
    const next = list.slice();
    next[idx] = { ...list[idx], ...incoming };
    return next;
  }
  const next = [...list, incoming];
  next.sort((a, b) => toTimeMs(a.createdAt) - toTimeMs(b.createdAt));
  return next;
};

const CHAT_BG = `
  radial-gradient(900px 520px at 12% -8%, rgba(var(--ion-color-primary-rgb), 0.2), transparent 64%),
  radial-gradient(860px 500px at 88% 4%, rgba(var(--ion-color-secondary-rgb), 0.16), transparent 68%),
  radial-gradient(760px 460px at 50% 100%, rgba(var(--ion-color-tertiary-rgb), 0.11), transparent 72%),
  linear-gradient(160deg, rgba(4, 10, 24, 0.99) 0%, rgba(8, 19, 41, 0.97) 48%, rgba(14, 30, 62, 0.95) 100%),
  var(--vault-surface)
`;

const ChatPage: React.FC<Props> = ({ match }) => {
  const { getToken, userId } = useAuth();
  const conversationId = match.params.conversationId;
  const { t } = useI18n();

  const [messages, setMessages] = useState<Message[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState<Blob | null>(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [recordedAudioMimeType, setRecordedAudioMimeType] = useState<string | null>(
    null
  );
  const [recordedAudioDurationSec, setRecordedAudioDurationSec] = useState(0);
  const [audioValidationError, setAudioValidationError] = useState<string | null>(
    null
  );
  const [audioPlaybackByMessage, setAudioPlaybackByMessage] = useState<
    Record<string, AudioPlaybackState>
  >({});
  const [decodedTextByMessage, setDecodedTextByMessage] = useState<
    Record<string, string>
  >({});
  const [decryptedAudioUrlByMessage, setDecryptedAudioUrlByMessage] = useState<
    Record<string, string>
  >({});

  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [readState, setReadState] = useState<ReadStateResponse | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [lastPostedReadMessageId, setLastPostedReadMessageId] = useState<
    string | null
  >(null);
  const [hasScrolledToAnchor, setHasScrolledToAnchor] = useState(false);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const [typingUserIds, setTypingUserIds] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [composerMode, setComposerMode] = useState<ComposerMode>('plain');
  const [isKeyPopoverOpen, setIsKeyPopoverOpen] = useState(false);
  const [showPattern, setShowPattern] = useState<boolean>(() => {
    return localStorage.getItem('chat:showDiagonalPattern') !== '0';
  });
  const [messageMenuFor, setMessageMenuFor] = useState<Message | null>(null);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [deleteInProgressFor, setDeleteInProgressFor] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showShareSheet, setShowShareSheet] = useState(false);

  const scrollInfoRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
  } | null>(null);
  const isAtBottomRef = useRef(true);
  const shouldScrollToBottomRef = useRef(false);
  const scrollBottomGenRef = useRef(0);
  const messageLongPressTimeoutRef = useRef<number | null>(null);
  const localTypingTimeoutRef = useRef<number | null>(null);
  const typingTimeoutsRef = useRef<Record<string, number>>({});
  const [revealCodedForMessage, setRevealCodedForMessage] = useState<Message | null>(
    null
  );
  const [shareUnlockInProgress, setShareUnlockInProgress] = useState(false);
  const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<number | null>(null);
  /** Keep live unghost/unlock notices visible briefly even if auto-read advances. */
  const [liveAvailabilityNotices, setLiveAvailabilityNotices] = useState<
    AvailabilityActivity[]
  >([]);
  const [dismissedAvailabilityIds, setDismissedAvailabilityIds] = useState<
    Set<string>
  >(() => new Set());
  const [dismissAvailabilityTarget, setDismissAvailabilityTarget] =
    useState<AvailabilityActivity | null>(null);
  const pushLiveAvailabilityNotice = useCallback(
    (
      kind: AvailabilityKind,
      messageId: string,
      at: string,
      actorLabel: string
    ) => {
      const id = `${kind}-${messageId}-${at}`;
      setLiveAvailabilityNotices((prev) => {
        if (prev.some((e) => e.id === id)) return prev;
        return [...prev, { id, kind, messageId, at, actorLabel }];
      });
      window.setTimeout(() => {
        setLiveAvailabilityNotices((prev) => prev.filter((e) => e.id !== id));
      }, 45000);
    },
    []
  );
  const pushLiveAvailabilityNoticeRef = useRef(pushLiveAvailabilityNotice);
  pushLiveAvailabilityNoticeRef.current = pushLiveAvailabilityNotice;
  const [revealingMessageId, setRevealingMessageId] = useState<string | null>(null);
  const [membersPopoverOpen, setMembersPopoverOpen] = useState(false);
  const [conversationName, setConversationName] = useState<string>('');
  const [members, setMembers] = useState<ConversationMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  /** Set when bootstrap fetch finishes for this id; avoids showing the previous chat’s title for one frame. */
  const [chatHeaderReadyForId, setChatHeaderReadyForId] = useState<string | null>(
    null
  );
  const titleLongPressTimeoutRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingStartTsRef = useRef<number | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const audioElementsRef = useRef<Record<string, HTMLAudioElement | null>>({});
  /** Per-message DEKs held client-side so Reveal can backfill if vault write failed. */
  const codedUnlockByMessageIdRef = useRef<Record<string, string>>({});
  const codedUnlockStorageKey = (id: string) => `itys:coded-unlock:${id}`;
  const rememberCodedUnlock = (messageId: string, key: string) => {
    const normalized = key.trim();
    if (!messageId || !normalized) return;
    codedUnlockByMessageIdRef.current[messageId] = normalized;
    try {
      sessionStorage.setItem(codedUnlockStorageKey(messageId), normalized);
    } catch {
      // ignore quota / private mode
    }
  };
  const lookupCodedUnlock = (messageId: string, fallback?: string | null) => {
    const fromMsg = fallback?.trim() || '';
    if (fromMsg) return fromMsg;
    const fromRef = codedUnlockByMessageIdRef.current[messageId]?.trim() || '';
    if (fromRef) return fromRef;
    try {
      return sessionStorage.getItem(codedUnlockStorageKey(messageId))?.trim() || '';
    } catch {
      return '';
    }
  };
  const hydrateCodedUnlockKeys = (list: Message[]): Message[] => {
    let changed = false;
    const next = list.map((m) => {
      if (m.decodeKey?.trim()) return m;
      const key = lookupCodedUnlock(m.id);
      if (!key) return m;
      changed = true;
      return { ...m, decodeKey: key };
    });
    return changed ? next : list;
  };

  const deriveSeed = (key: string): number => {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < key.length; i++) {
      h ^= key.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
  };

  const xorWithKeyStream = (text: string, key: string): string => {
    const seed = deriveSeed(key);
    let state = seed;
    let out = '';
    for (let i = 0; i < text.length; i++) {
      state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
      const kc = state & 0xff;
      out += String.fromCharCode(text.charCodeAt(i) ^ kc);
    }
    return out;
  };

  const decodeLegacyText = (content: string | null, key: string): string | null => {
    if (!content || !key || !content.startsWith('[[enc]]')) return null;
    try {
      const cipher = atob(content.slice('[[enc]]'.length));
      return xorWithKeyStream(cipher, key);
    } catch {
      return null;
    }
  };

  const parseSharedKey = (
    content: string | null
  ): { key: string; messageId?: string } | null => {
    if (!content) return null;
    if (!content.startsWith('[[key')) return null;

    const endHeader = content.indexOf(']]');
    if (endHeader === -1) return null;

    const header = content.slice(2, endHeader); // e.g. "key" or "key:<messageId>"
    const rest = content.slice(endHeader + 2);

    if (!header.startsWith('key')) return null;

    let messageId: string | undefined;
    const parts = header.split(':', 2);
    if (parts.length === 2 && parts[1]) {
      messageId = parts[1];
    }

    const base = rest;
    try {
      const key = atob(base);
      return { key, messageId };
    } catch {
      return null;
    }
  };

  /** Apply keys from in-thread `[[key:messageId]]` share cards onto target messages. */
  const applySharedKeysLocally = (list: Message[]): Message[] => {
    const keyByTarget = new Map<string, string>();
    for (const m of list) {
      const shared = parseSharedKey(m.content);
      const key = shared?.key?.trim();
      if (key && shared?.messageId) {
        keyByTarget.set(shared.messageId, key);
      }
    }
    if (keyByTarget.size === 0) return list;
    let changed = false;
    const next = list.map((m) => {
      const key = keyByTarget.get(m.id);
      if (!key || m.decodeKey?.trim()) return m;
      changed = true;
      return { ...m, decodeKey: key };
    });
    return changed ? next : list;
  };

  const applyDecodeKeyToMessage = (messageId: string, key: string, unlockedAt?: string | null) => {
    const normalized = key.trim();
    if (!normalized || !messageId) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId
          ? {
              ...m,
              decodeKey: normalized,
              unlockedAt: unlockedAt || m.unlockedAt || new Date().toISOString(),
            }
          : m
      )
    );
  };
  const applyDecodeKeyToMessageRef = useRef(applyDecodeKeyToMessage);
  applyDecodeKeyToMessageRef.current = applyDecodeKeyToMessage;

  /** Ionic often drops a second popover if opened in the same tick as dismissing another. */
  const openAfterMenuClose = (open: () => void) => {
    window.setTimeout(open, 160);
  };

  const handleScroll = (e: CustomEvent) => {
    const detail: any = (e as any).detail;
    if (!detail) return;
    const { scrollTop, scrollHeight, clientHeight } = detail;
    scrollInfoRef.current = { scrollTop, scrollHeight, clientHeight };
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    // Treat the user as "at bottom" even with a small slack area
    const atBottom = distanceFromBottom < 160;
    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
  };

  /** Pin to bottom; retries after layout (new bubbles, availability notices). */
  const scrollToBottom = useCallback((reason?: string) => {
    void reason;
    const content = contentRef.current;
    if (!content) return;
    const gen = ++scrollBottomGenRef.current;

    const pin = (el: HTMLElement) => {
      if (gen !== scrollBottomGenRef.current) return;
      el.scrollTop = el.scrollHeight;
      scrollInfoRef.current = {
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
      };
      isAtBottomRef.current = true;
    };

    void content.getScrollElement().then((el) => {
      pin(el);
      requestAnimationFrame(() => {
        pin(el);
        requestAnimationFrame(() => pin(el));
      });
      window.setTimeout(() => pin(el), 50);
      window.setTimeout(() => {
        pin(el);
        if (gen === scrollBottomGenRef.current) {
          setIsAtBottom(true);
        }
      }, 180);
    });
  }, []);

  const requestScrollToBottom = useCallback(() => {
    shouldScrollToBottomRef.current = true;
    scrollToBottom('request');
  }, [scrollToBottom]);

  // WebSocket connection for realtime messages + typing
  useEffect(() => {
    let ws: WebSocket | null = null;
    let cancelled = false;

    async function connect() {
      const token = await getToken();
      if (cancelled) return;

      // Force version gate only wraps fetch; skip realtime when API is blocked.
      const { isApiBlocked } = await import('../lib/apiGate');
      if (isApiBlocked()) return;

      const url = new URL(apiUrl('/api/chat/realtime'));
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      if (token) {
        url.searchParams.set('token', token);
      }

      ws = new WebSocket(url.toString());
      socketRef.current = ws;
      setSocket(ws);

      ws.onopen = () => {
        ws?.send(
          JSON.stringify({
            type: 'subscribe',
            conversationId,
          })
        );
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'message:new' && data.conversationId === conversationId) {
            const incoming: Message = data.message;

            setMessages((prev) => {
              if (prev.some((m) => m.id === incoming.id)) {
                return prev;
              }
              return [...prev, incoming];
            });

            // Legacy in-thread key cards (enc2 era)
            const shared = parseSharedKey(incoming.content ?? null);
            if (shared) {
              const key = shared.key.trim();
              const targetId = shared.messageId;
              if (key && targetId) {
                applyDecodeKeyToMessageRef.current(targetId, key);
              }
            }

            // Pin only when already near bottom (don't yank while reading history).
            if (isAtBottomRef.current) {
              shouldScrollToBottomRef.current = true;
            }
          }

          if (
            data.type === 'message:unlock' &&
            data.conversationId === conversationId &&
            typeof data.messageId === 'string' &&
            typeof data.key === 'string'
          ) {
            const unlockedAt =
              typeof data.unlockedAt === 'string'
                ? data.unlockedAt
                : new Date().toISOString();
            applyDecodeKeyToMessageRef.current(data.messageId, data.key, unlockedAt);
            pushLiveAvailabilityNoticeRef.current(
              'unlock',
              data.messageId,
              unlockedAt,
              t('chat.sender.someone')
            );
            if (isAtBottomRef.current) {
              shouldScrollToBottomRef.current = true;
            }
          }

          if (
            data.type === 'message:revealed' &&
            data.conversationId === conversationId
          ) {
            const incoming: Message = data.message;
            setMessages((prev) => upsertMessageByCreatedAt(prev, incoming));
            const at = incoming.revealedAt || new Date().toISOString();
            const actor =
              incoming.senderId === userId
                ? t('chat.sender.you')
                : [incoming.sender?.firstName, incoming.sender?.lastName]
                    .filter(Boolean)
                    .join(' ')
                    .trim() ||
                  incoming.sender?.email ||
                  t('chat.sender.someone');
            pushLiveAvailabilityNoticeRef.current(
              'unghost',
              incoming.id,
              at,
              actor
            );
            if (isAtBottomRef.current) {
              shouldScrollToBottomRef.current = true;
            }
          }

          if (data.type === 'typing' && data.conversationId === conversationId) {
            const typingUserId: string = data.userId;
            // Ignore missing ids and our own events (e.g. second tab).
            if (!typingUserId || typingUserId === userId) {
              return;
            }

            setTypingUserIds((prev) => {
              const exists = prev.includes(typingUserId);

              if (data.isTyping) {
                return exists ? prev : [...prev, typingUserId];
              }

              // isTyping === false -> remove from list
              return prev.filter((id) => id !== typingUserId);
            });

            // Manage per-user typing timeout so stale entries are removed
            if (data.isTyping) {
              const timeouts = typingTimeoutsRef.current;
              if (timeouts[typingUserId]) {
                window.clearTimeout(timeouts[typingUserId]);
              }
              timeouts[typingUserId] = window.setTimeout(() => {
                setTypingUserIds((prev) =>
                  prev.filter((id) => id !== typingUserId)
                );
                delete typingTimeoutsRef.current[typingUserId];
              }, 3000);
            } else {
              const timeouts = typingTimeoutsRef.current;
              if (timeouts[typingUserId]) {
                window.clearTimeout(timeouts[typingUserId]);
                delete timeouts[typingUserId];
              }
            }
          }
        } catch {
          // ignore malformed events
        }
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (localTypingTimeoutRef.current) {
        window.clearTimeout(localTypingTimeoutRef.current);
        localTypingTimeoutRef.current = null;
      }
      Object.values(typingTimeoutsRef.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      typingTimeoutsRef.current = {};
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
      if (socketRef.current === ws) {
        socketRef.current = null;
      }
      setSocket(null);
      setTypingUserIds([]);
    };
  }, [conversationId, getToken, userId]);

  useEffect(() => {
    const ac = new AbortController();
    let isMounted = true;

    async function loadInitial() {
      try {
        setInitialLoading(true);
        setChatHeaderReadyForId(null);
        setError(null);

        const token = await getToken();
        const jsonHeaders = {
          'Content-Type': 'application/json',
          ...apiHeaders(token),
        };
        const { signal } = ac;
        const bootstrapRes = await fetch(
          apiUrl(`/api/chat/conversations/${conversationId}/bootstrap`),
          {
            headers: jsonHeaders,
            signal,
          }
        );
        if (!bootstrapRes.ok) {
          throw new Error(`Failed to load chat (${bootstrapRes.status})`);
        }
        const boot = (await bootstrapRes.json()) as ChatBootstrapResponse;
        const messagesJson = boot.messages;
        const readStateJson = boot.readState;
        const membersJson = boot.members;

        if (isMounted) {
          setLiveAvailabilityNotices([]);
          setMessages(hydrateCodedUnlockKeys(applySharedKeysLocally(messagesJson.data)));
          setNextCursor(messagesJson.nextCursor);
          setReadState(readStateJson);
          setConversationName(
            typeof membersJson.conversationName === 'string'
              ? membersJson.conversationName
              : ''
          );
          setMembers(membersJson.data || []);
        }
      } catch (err) {
        if (ac.signal.aborted) {
          return;
        }
        if (isMounted) {
          setError(
            err instanceof Error ? err.message : t('chat.error.loadFailed')
          );
        }
      } finally {
        if (isMounted && !ac.signal.aborted) {
          setInitialLoading(false);
          setChatHeaderReadyForId(conversationId);
        }
      }
    }

    loadInitial();

    return () => {
      isMounted = false;
      ac.abort();
    };
  }, [getToken, conversationId]);

  useEffect(() => {
    setConversationName('');
    setReplyingTo(null);
    setHighlightMessageId(null);
    setDismissedAvailabilityIds(new Set());
    setDismissAvailabilityTarget(null);
    setLiveAvailabilityNotices([]);
    setHasScrolledToAnchor(false);
    setIsAtBottom(true);
    isAtBottomRef.current = true;
    shouldScrollToBottomRef.current = false;
    scrollBottomGenRef.current += 1;
  }, [conversationId]);

  useEffect(() => {
    return () => {
      if (messageLongPressTimeoutRef.current !== null) {
        window.clearTimeout(messageLongPressTimeoutRef.current);
        messageLongPressTimeoutRef.current = null;
      }
      if (titleLongPressTimeoutRef.current !== null) {
        window.clearTimeout(titleLongPressTimeoutRef.current);
        titleLongPressTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('chat:showDiagonalPattern', showPattern ? '1' : '0');
  }, [showPattern]);

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;

    try {
      setLoadingMore(true);
      const previousScroll = scrollInfoRef.current;
      const token = await getToken();
      const res = await fetch(
        apiUrl(
          `/api/chat/conversations/${conversationId}/messages?cursor=${encodeURIComponent(
            nextCursor
          )}`
        ),
        {
          headers: {
            'Content-Type': 'application/json',
            ...apiHeaders(token),
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to load more messages (${res.status})`);
      }

      const json = (await res.json()) as MessagesResponse;
      setMessages((prev) =>
        hydrateCodedUnlockKeys(applySharedKeysLocally([...json.data, ...prev]))
      );
      setNextCursor(json.nextCursor);

      // Preserve scroll position so user stays near where they clicked "Load older"
      if (previousScroll && contentRef.current) {
        requestAnimationFrame(() => {
          const contentEl = contentRef.current;
          if (!contentEl) return;
          contentEl.getScrollElement().then((el) => {
            const newScrollHeight = el.scrollHeight;
            const delta = newScrollHeight - previousScroll.scrollHeight;
            el.scrollTop = previousScroll.scrollTop + delta;
          });
        });
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('chat.error.loadMoreFailed')
      );
    } finally {
      setLoadingMore(false);
    }
  };

  const cleanupRecorder = () => {
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    mediaRecorderRef.current = null;
    recordingChunksRef.current = [];
    recordingStartTsRef.current = null;
    setIsRecordingAudio(false);
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    } else {
      cleanupRecorder();
    }
  };

  const startRecording = async () => {
    if (isRecordingAudio || sending) return;
    setAudioValidationError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType =
        SUPPORTED_AUDIO_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ||
        '';
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      recordingChunksRef.current = [];
      recordingStartTsRef.current = Date.now();
      setRecordingSeconds(0);
      setRecordedAudioBlob(null);
      setRecordedAudioDurationSec(0);
      setRecordedAudioMimeType(recorder.mimeType || mimeType || 'audio/webm');
      if (recordedAudioUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
        setRecordedAudioUrl(null);
      }

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        const chunks = recordingChunksRef.current;
        const blob = new Blob(chunks, {
          type: recorder.mimeType || recordedAudioMimeType || 'audio/webm',
        });
        const durationSec = Math.min(
          MAX_AUDIO_DURATION_SEC,
          Math.floor((Date.now() - (recordingStartTsRef.current || Date.now())) / 1000)
        );
        setRecordedAudioBlob(blob);
        setRecordedAudioDurationSec(durationSec);
        setRecordedAudioMimeType(blob.type || recorder.mimeType || 'audio/webm');
        const blobUrl = URL.createObjectURL(blob);
        setRecordedAudioUrl(blobUrl);
        stream.getTracks().forEach((track) => track.stop());
        cleanupRecorder();
      };
      recorder.onerror = () => {
        stream.getTracks().forEach((track) => track.stop());
        setAudioValidationError(t('chat.audio.error.recordingFailed'));
        cleanupRecorder();
      };

      mediaRecorderRef.current = recorder;
      recorder.start(200);
      setIsRecordingAudio(true);
      recordingTimerRef.current = window.setInterval(() => {
        const elapsedSec = Math.floor(
          (Date.now() - (recordingStartTsRef.current || Date.now())) / 1000
        );
        setRecordingSeconds(elapsedSec);
        if (elapsedSec >= MAX_AUDIO_DURATION_SEC) {
          setAudioValidationError(t('chat.audio.error.durationLimit'));
          stopRecording();
        }
      }, 250);
    } catch {
      setAudioValidationError(t('chat.audio.error.microphonePermission'));
      cleanupRecorder();
    }
  };

  const discardRecordedAudio = () => {
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
    }
    setRecordedAudioUrl(null);
    setRecordedAudioBlob(null);
    setRecordedAudioDurationSec(0);
    setRecordedAudioMimeType(null);
    setRecordingSeconds(0);
    setAudioValidationError(null);
  };

  const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const value = reader.result;
        if (typeof value === 'string') {
          resolve(value);
          return;
        }
        reject(new Error('Could not read audio blob'));
      };
      reader.onerror = () => reject(new Error('Could not read audio blob'));
      reader.readAsDataURL(blob);
    });

  const handleSendAudio = async () => {
    const replyTarget = replyingTo;
    if (!recordedAudioBlob || sending || isRecordingAudio) return;
    if (recordedAudioDurationSec <= 0 || recordedAudioDurationSec > MAX_AUDIO_DURATION_SEC) {
      setAudioValidationError(t('chat.audio.error.durationLimit'));
      return;
    }
    try {
      setSending(true);
      setAudioValidationError(null);
      const token = await getToken();
      const mimeType = recordedAudioMimeType || recordedAudioBlob.type || 'audio/webm';
      let content: string | null = null;
      let url: string;
      let unlockKey: string | undefined;
      if (composerMode === 'coded') {
        unlockKey = generateMessageDek();
        const bytes = new Uint8Array(await recordedAudioBlob.arrayBuffer());
        const encrypted = await encryptAudioV3(bytes, unlockKey, mimeType);
        content = encrypted.content;
        url = encrypted.encryptedDataUrl;
      } else {
        url = await blobToDataUrl(recordedAudioBlob);
      }
      const res = await fetch(
        apiUrl(`/api/chat/conversations/${conversationId}/messages`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...apiHeaders(token),
          },
          body: JSON.stringify({
            type: 'AUDIO',
            content,
            unlockKey,
            isHidden: composerMode === 'ghost',
            revealType: composerMode === 'ghost' ? 'MANUAL' : undefined,
            replyToMessageId: replyTarget?.id ?? undefined,
            attachments: [
              {
                url,
                type: mimeType,
                size: recordedAudioBlob.size,
                sizeBytes: recordedAudioBlob.size,
                durationSec: recordedAudioDurationSec,
                mimeType,
              },
            ],
          }),
        }
      );
      if (!res.ok) {
        throw new Error(`Failed to send audio (${res.status})`);
      }
      const saved = (await res.json()) as Message;
      setReplyingTo(null);
      discardRecordedAudio();
      if (saved?.id) {
        if (unlockKey) {
          rememberCodedUnlock(saved.id, unlockKey);
          if (!saved.decodeKey) saved.decodeKey = unlockKey;
        }
        setMessages((prev) => {
          if (prev.some((m) => m.id === saved.id)) {
            return prev.map((m) => (m.id === saved.id ? { ...m, ...saved } : m));
          }
          return [...prev, saved];
        });
        requestScrollToBottom();
      }
    } catch (err) {
      setAudioValidationError(
        err instanceof Error ? err.message : t('chat.audio.error.sendFailed')
      );
    } finally {
      setSending(false);
    }
  };

  const toggleAudioPlayback = (messageId: string) => {
    const el = audioElementsRef.current[messageId];
    if (!el) return;
    if (el.paused) {
      void el.play();
    } else {
      el.pause();
    }
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;

    const rawContent = input.trim();
    const replyTarget = replyingTo;
    setInput('');

    const replySnapshot = replyTarget
      ? {
          id: replyTarget.id,
          senderId: replyTarget.senderId,
          sender: replyTarget.sender,
          content: replyTarget.content,
          type: replyTarget.type,
          attachments: replyTarget.attachments,
          isHidden: replyTarget.isHidden,
          revealedAt: replyTarget.revealedAt ?? null,
          decodeKey: replyTarget.decodeKey ?? null,
          revealType: replyTarget.revealType,
          canReveal: replyTarget.canReveal,
        }
      : null;

    const optimistic: Message = {
      id: `temp-${Date.now()}`,
      content: rawContent,
      type: 'TEXT',
      createdAt: new Date().toISOString(),
      senderId: userId || 'me',
      sender: null,
      attachments: [],
      replyToMessageId: replyTarget?.id ?? null,
      replyTo: replySnapshot,
      isHidden: composerMode === 'ghost',
      revealedAt: null,
      revealType: 'MANUAL',
      canReveal: true,
    };

    setMessages((prev) => [...prev, optimistic]);
    requestScrollToBottom();

    try {
      setSending(true);
      let content = rawContent;
      let unlockKey: string | undefined;
      if (composerMode === 'coded') {
        unlockKey = generateMessageDek();
        content = await encryptTextV3(rawContent, unlockKey);
      }
      const token = await getToken();
      const res = await fetch(
        apiUrl(`/api/chat/conversations/${conversationId}/messages`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...apiHeaders(token),
          },
          body: JSON.stringify({
            type: 'TEXT',
            content,
            unlockKey,
            isHidden: composerMode === 'ghost',
            revealType: composerMode === 'ghost' ? 'MANUAL' : undefined,
            replyToMessageId: replyTarget?.id ?? undefined,
          }),
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to send message (${res.status})`);
      }

      const saved = (await res.json()) as Message;
      if (unlockKey && saved?.id) {
        rememberCodedUnlock(saved.id, unlockKey);
        if (!saved.decodeKey) saved.decodeKey = unlockKey;
      }
      setReplyingTo(null);
      setMessages((prev) => {
        // If the realtime WebSocket already delivered this message,
        // drop the optimistic one and keep the existing saved copy.
        if (prev.some((m) => m.id === saved.id)) {
          return prev.filter((m) => m.id !== optimistic.id).map((m) =>
            m.id === saved.id ? { ...m, ...saved } : m
          );
        }
        return prev.map((m) => (m.id === optimistic.id ? saved : m));
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('chat.error.sendFailed')
      );
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setInput(rawContent);
      if (replyTarget) setReplyingTo(replyTarget);
    } finally {
      setSending(false);
    }
  };

  const handleRevealMessage = async (messageId: string) => {
    if (revealingMessageId) return;
    try {
      setRevealingMessageId(messageId);
      const token = await getToken();
      const res = await fetch(apiUrl(`/api/chat/messages/${messageId}/reveal`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...apiHeaders(token),
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        throw new Error(`Failed to reveal message (${res.status})`);
      }

      const revealed = (await res.json()) as Message;
      setMessages((prev) => upsertMessageByCreatedAt(prev, revealed));
      const at = revealed.revealedAt || new Date().toISOString();
      pushLiveAvailabilityNotice(
        'unghost',
        revealed.id,
        at,
        t('chat.sender.you')
      );
      setToastMessage(t('chat.menu.feedback.unghosted'));
      requestScrollToBottom();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('chat.error.revealFailed')
      );
    } finally {
      setRevealingMessageId(null);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (deleteInProgressFor) return;
    try {
      setDeleteInProgressFor(messageId);
      const token = await getToken();
      const res = await fetch(apiUrl(`/api/chat/messages/${messageId}`), {
        method: 'DELETE',
        headers: {
          ...apiHeaders(token),
        },
      });
      if (!res.ok) {
        throw new Error(`Failed to delete message (${res.status})`);
      }
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      setToastMessage(t('chat.menu.feedback.deleted'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('chat.error.deleteFailed'));
    } finally {
      setDeleteInProgressFor(null);
    }
  };

  const getMemberLabel = (member: ConversationMember): string => {
    const fullName = [member.user.firstName, member.user.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return fullName || member.user.email;
  };

  const openMembersPopover = async () => {
    setMembersPopoverOpen(true);
    if (members.length > 0) return;
    try {
      setMembersLoading(true);
      const token = await getToken();
      const res = await fetch(
        apiUrl(`/api/chat/conversations/${conversationId}/members`),
        {
          headers: {
            'Content-Type': 'application/json',
            ...apiHeaders(token),
          },
        }
      );
      if (!res.ok) return;
      const json = (await res.json()) as ConversationMembersResponse;
      if (typeof json.conversationName === 'string') {
        setConversationName(json.conversationName);
      }
      setMembers(json.data || []);
    } catch {
      // best-effort
    } finally {
      setMembersLoading(false);
    }
  };

  const LONG_PRESS_MS = 550;

  const scheduleLongPress = (
    timerRef: React.MutableRefObject<number | null>,
    action: () => void
  ) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      action();
    }, LONG_PRESS_MS);
  };

  const cancelLongPress = (timerRef: React.MutableRefObject<number | null>) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTitleLongPress = () => {
    scheduleLongPress(titleLongPressTimeoutRef, () => {
      openMembersPopover();
    });
  };

  const cancelTitleLongPress = () => {
    cancelLongPress(titleLongPressTimeoutRef);
  };

  const startMessageLongPress = (m: Message) => {
    scheduleLongPress(messageLongPressTimeoutRef, () => {
      setMessageMenuFor(m);
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try {
          navigator.vibrate(12);
        } catch {
          // ignore
        }
      }
    });
  };

  const cancelMessageLongPress = () => {
    cancelLongPress(messageLongPressTimeoutRef);
  };

  const getSenderLabel = (message: Message): string => {
    if (message.senderId === userId) return t('chat.sender.you');
    const fullName =
      [message.sender?.firstName, message.sender?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
    if (fullName) return fullName;
    return message.sender?.email || t('chat.sender.unknown');
  };

  const scrollToMessageAndHighlight = (messageId: string) => {
    const el = document.getElementById(`msg-${messageId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setHighlightMessageId(messageId);
    if (highlightTimeoutRef.current !== null) {
      window.clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightMessageId((cur) => (cur === messageId ? null : cur));
      highlightTimeoutRef.current = null;
    }, 1800);
    setLiveAvailabilityNotices((prev) =>
      prev.filter((e) => e.messageId !== messageId)
    );
  };

  const availabilityActivities = useMemo((): AvailabilityActivity[] => {
    const lastReadMs = toTimeMs(readState?.lastReadAt);
    const fromHistory: AvailabilityActivity[] = [];
    for (const m of messages) {
      const isMine =
        (!!userId && m.senderId === userId) ||
        (!!userId && m.sender?.id === userId);
      const actorLabel = isMine ? t('chat.sender.you') : getSenderLabel(m);
      if (m.isHidden && m.revealedAt && toTimeMs(m.revealedAt) > lastReadMs) {
        fromHistory.push({
          id: `unghost-${m.id}-${m.revealedAt}`,
          kind: 'unghost',
          messageId: m.id,
          at: m.revealedAt,
          actorLabel,
        });
      }
      if (m.unlockedAt && toTimeMs(m.unlockedAt) > lastReadMs) {
        fromHistory.push({
          id: `unlock-${m.id}-${m.unlockedAt}`,
          kind: 'unlock',
          messageId: m.id,
          at: m.unlockedAt,
          actorLabel,
        });
      }
    }
    const merged = new Map<string, AvailabilityActivity>();
    for (const ev of [...fromHistory, ...liveAvailabilityNotices]) {
      if (dismissedAvailabilityIds.has(ev.id)) continue;
      merged.set(ev.id, ev);
    }
    return Array.from(merged.values()).sort(
      (a, b) => toTimeMs(a.at) - toTimeMs(b.at)
    );
  }, [
    messages,
    readState?.lastReadAt,
    t,
    userId,
    liveAvailabilityNotices,
    dismissedAvailabilityIds,
  ]);

  const confirmDismissAvailability = (ev: AvailabilityActivity) => {
    setDismissedAvailabilityIds((prev) => {
      const next = new Set(prev);
      next.add(ev.id);
      return next;
    });
    setLiveAvailabilityNotices((prev) => prev.filter((e) => e.id !== ev.id));
    setDismissAvailabilityTarget(null);
  };

  const getReplySnippetFromPayload = (
    r: NonNullable<Message['replyTo']>
  ): string => {
    if (r.unavailable) {
      return t('chat.reply.originalUnavailable');
    }
    if (r.type === 'IMAGE') return t('chat.reply.type.image');
    if (r.type === 'FILE') return t('chat.reply.type.file');
    if (r.type === 'AUDIO') return t('chat.reply.type.audio');
    const c = r.content ?? '';
    if (
      c.startsWith('[[enc2]]') ||
      c.startsWith('[[enc]]') ||
      c.startsWith('[[enc-audio2]]')
    ) {
      return t('chat.reply.type.encoded');
    }
    return c.trim() || t('chat.message.deleted');
  };

  const getReplySnippetForCompose = (target: Message): string => {
    if (parseSharedKey(target.content ?? null)) {
      return t('chat.reply.type.keyShare');
    }
    const isEnc =
      !!target.content?.startsWith('[[enc2]]') ||
      !!target.content?.startsWith('[[enc]]');
    if (isEnc) {
      const dec = decodedTextByMessage[target.id];
      return dec || t('chat.reply.type.encoded');
    }
    if (target.type === 'IMAGE') return t('chat.reply.type.image');
    if (target.type === 'FILE') return t('chat.reply.type.file');
    if (target.type === 'AUDIO') return t('chat.reply.type.audio');
    return (target.content || '').trim() || t('chat.message.deleted');
  };

  const getReplyToAuthorLabel = (r: NonNullable<Message['replyTo']>): string => {
    if (r.unavailable) return '';
    if (r.senderId === userId) return t('chat.sender.you');
    const fullName = [r.sender?.firstName, r.sender?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (fullName) return fullName;
    return r.sender?.email || t('chat.sender.unknown');
  };

  const canOfferReplyToMessage = (m: Message): boolean => {
    if (!userId) return false;
    const isMine = m.senderId === userId || m.sender?.id === userId;
    const isHiddenUnrevealed = !!m.isHidden && !m.revealedAt;
    if (isHiddenUnrevealed && !isMine) return false;
    if (parseSharedKey(m.content ?? null)) return false;
    if (m.content?.startsWith('[[key')) return false;
    return true;
  };

  const scrollToRepliedMessage = useCallback(
    (targetId: string) => {
      const el = document.getElementById(`msg-${targetId}`);
      if (!el) {
        setToastMessage(t('chat.reply.notInView'));
        return;
      }
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setHighlightMessageId(targetId);
      window.setTimeout(() => {
        setHighlightMessageId((cur) => (cur === targetId ? null : cur));
      }, 1600);
    },
    [t]
  );

  const renderReplyQuote = (m: Message): React.ReactNode => {
    if (!m.replyTo) return null;
    const r = m.replyTo;
    const author = getReplyToAuthorLabel(r);
    const dec = !r.unavailable ? decodedTextByMessage[r.id] : undefined;
    const snippet = r.unavailable
      ? t('chat.reply.originalUnavailable')
      : dec || getReplySnippetFromPayload(r);
    const canNavigate = !!r.id && !r.unavailable;
    return (
      <div
        className={`message-reply-quote${
          canNavigate ? ' message-reply-quote--clickable' : ''
        }`}
        role={canNavigate ? 'button' : undefined}
        tabIndex={canNavigate ? 0 : undefined}
        aria-label={canNavigate ? t('chat.reply.goToOriginalAria') : undefined}
        onClick={
          canNavigate
            ? (e) => {
                e.stopPropagation();
                scrollToRepliedMessage(r.id);
              }
            : undefined
        }
        onKeyDown={
          canNavigate
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollToRepliedMessage(r.id);
                }
              }
            : undefined
        }
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div className="message-reply-quote-bar" aria-hidden="true" />
        <div className="message-reply-quote-inner">
          {author ? (
            <span className="message-reply-quote-author">{author}</span>
          ) : null}
          <span className="message-reply-quote-snippet">{snippet}</span>
        </div>
      </div>
    );
  };

  const getTypingLabel = (): string | null => {
    const others = typingUserIds.filter((id) => id && id !== userId);
    if (!others.length) return null;

    const names = others.map((id) => {
      const member = members.find((m) => m.userId === id);
      if (member?.user) {
        const fullName = [member.user.firstName, member.user.lastName]
          .filter(Boolean)
          .join(' ')
          .trim();
        if (fullName) return fullName;
        if (member.user.email) return member.user.email;
      }
      const msg = messages.find((m) => m.senderId === id);
      return msg ? getSenderLabel(msg) : t('chat.sender.someone');
    });

    // Deduplicate display names
    const uniqueNames = Array.from(new Set(names));
    const count = uniqueNames.length;
    if (count === 0) return null;

    const maxShown = 3;
    const shown = uniqueNames.slice(0, maxShown);
    const remaining = count - shown.length;

    if (count === 1) {
      return t('chat.typing.single', { name: shown[0] });
    }

    if (count === 2) {
      return t('chat.typing.multiple', { names: `${shown[0]}, ${shown[1]}` });
    }

    if (count === 3 && remaining === 0) {
      return t('chat.typing.multiple', {
        names: `${shown[0]}, ${shown[1]} and ${shown[2]}`,
      });
    }

    // 4 or more total names
    return t('chat.typing.multiple', {
      names: `${shown[0]}, ${shown[1]}, ${shown[2]} and ${remaining} more`,
    });
  };

  const typingLabel = getTypingLabel();
  const diagonalPatternLines = useMemo(() => {
    const lines: string[] = [];
    if (composerMode === 'coded') lines.push(t('chat.pattern.word.secret'));
    if (composerMode === 'ghost') lines.push(t('chat.pattern.word.ghost'));
    return lines;
  }, [composerMode, t]);
  // Plain text: no diagonal pattern. Toggle is kept but hidden for now.
  const patternVisible =
    showPattern && composerMode !== 'plain' && diagonalPatternLines.length > 0;
  const diagonalLineContent = (token: string) =>
    Array.from({ length: 20 }).map(() => token).join('   ');

  const sendTypingEvent = (typing: boolean) => {
    const activeSocket = socketRef.current ?? socket;
    if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) return;
    activeSocket.send(
      JSON.stringify({
        type: 'typing',
        conversationId,
        isTyping: typing,
      })
    );
  };

  const handleInputChange = (value: string) => {
    setInput(value);

    const activeSocket = socketRef.current ?? socket;
    if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) return;
    // Only advertise typing while composing plain text.
    if (composerMode !== 'plain') return;
    if (!value.trim()) {
      if (isTyping || localTypingTimeoutRef.current !== null) {
        if (localTypingTimeoutRef.current !== null) {
          window.clearTimeout(localTypingTimeoutRef.current);
          localTypingTimeoutRef.current = null;
        }
        setIsTyping(false);
        sendTypingEvent(false);
      }
      return;
    }

    if (!isTyping) {
      setIsTyping(true);
      sendTypingEvent(true);
    }

    if (localTypingTimeoutRef.current) {
      window.clearTimeout(localTypingTimeoutRef.current);
    }

    localTypingTimeoutRef.current = window.setTimeout(() => {
      setIsTyping(false);
      sendTypingEvent(false);
      localTypingTimeoutRef.current = null;
    }, 1500);
  };

  // Initial position: bottom when caught up; otherwise jump to last-read (instant, not smooth).
  useEffect(() => {
    if (initialLoading || hasScrolledToAnchor || !messages.length) return;

    const lastId = messages[messages.length - 1]?.id;
    const lastReadId = readState?.lastReadMessageId ?? null;
    const lastReadIdx = lastReadId
      ? messages.findIndex((m) => m.id === lastReadId)
      : -1;
    const hasUnreadAfterRead =
      lastReadIdx >= 0 && lastReadIdx < messages.length - 1;

    if (!hasUnreadAfterRead || !lastReadId || lastReadId === lastId) {
      scrollToBottom('initial-bottom');
      setHasScrolledToAnchor(true);
      return;
    }

    const el = document.getElementById(`msg-${lastReadId}`);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'auto' });
      isAtBottomRef.current = false;
      setIsAtBottom(false);
      setHasScrolledToAnchor(true);
      return;
    }

    scrollToBottom('initial-fallback');
    setHasScrolledToAnchor(true);
  }, [
    initialLoading,
    messages,
    readState?.lastReadMessageId,
    hasScrolledToAnchor,
    scrollToBottom,
  ]);

  useEffect(() => {
    return () => {
      if (recordedAudioUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
      }
      Object.values(decryptedAudioUrlByMessage).forEach((url) => URL.revokeObjectURL(url));
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
      }
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      }
    };
  }, [recordedAudioUrl, decryptedAudioUrlByMessage]);

  // Keep view pinned to bottom after sends / new messages / late layout (notices).
  // Important: do NOT depend on isAtBottom/readState — those were clearing the
  // scroll flag before the new message height was laid out.
  useEffect(() => {
    if (!hasScrolledToAnchor) return;
    if (!shouldScrollToBottomRef.current && !isAtBottomRef.current) return;
    shouldScrollToBottomRef.current = false;
    scrollToBottom('stick');
  }, [messages, availabilityActivities.length, hasScrolledToAnchor, scrollToBottom]);

  // When user is at bottom, advance read state past new messages and late availability events
  useEffect(() => {
    if (!messages.length) return;
    if (!isAtBottom) return;

    const last = messages[messages.length - 1];
    const maxAvailableMs = messages.reduce(
      (max, m) => Math.max(max, toTimeMs(getMessageAvailableAt(m))),
      0
    );
    const lastReadMs = toTimeMs(readState?.lastReadAt);
    if (
      lastPostedReadMessageId === last.id &&
      lastReadMs >= maxAvailableMs - 500
    ) {
      return;
    }

    async function update() {
      try {
        const token = await getToken();
        const res = await fetch(
          apiUrl(`/api/chat/conversations/${conversationId}/read-state`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...apiHeaders(token),
            },
            body: JSON.stringify({ lastReadMessageId: last.id }),
          }
        );

        if (res.ok) {
          const body = (await res.json().catch(() => null)) as
            | { lastReadAt?: string; lastReadMessageId?: string }
            | null;
          const readAt = body?.lastReadAt || new Date().toISOString();
          setLastPostedReadMessageId(last.id);
          setReadState((prev) =>
            prev
              ? {
                  ...prev,
                  lastReadMessageId: last.id,
                  lastReadAt: readAt,
                  unreadCount: 0,
                }
              : {
                  lastReadMessageId: last.id,
                  lastReadAt: readAt,
                  unreadCount: 0,
                }
          );
        }
      } catch {
        // best-effort; ignore errors
      }
    }

    update();
  }, [
    messages,
    isAtBottom,
    lastPostedReadMessageId,
    conversationId,
    getToken,
    readState?.lastReadAt,
  ]);

  useEffect(() => {
    let cancelled = false;
    const createdAudioUrls: string[] = [];

    async function decodeEncryptedMessages() {
      const nextDecodedText: Record<string, string> = {};
      const nextAudioUrls: Record<string, string> = {};

      for (const m of messages) {
        // Prefer per-message grant only (never a global composer key).
        const keyCandidate = m.decodeKey?.trim() || '';

        if (isEncryptedTextContent(m.content) && keyCandidate) {
          const plain =
            (await decryptTextAny(m.content, keyCandidate)) ||
            (m.content?.startsWith('[[enc]]')
              ? decodeLegacyText(m.content, keyCandidate)
              : null);
          if (plain != null) {
            nextDecodedText[m.id] = plain;
          }
        }

        if (
          m.type === 'AUDIO' &&
          isEncryptedAudioContent(m.content) &&
          m.attachments[0]?.url &&
          keyCandidate
        ) {
          const blob = await decryptAudioAny(
            m.content,
            m.attachments[0].url,
            keyCandidate
          );
          if (blob) {
            const url = URL.createObjectURL(blob);
            createdAudioUrls.push(url);
            nextAudioUrls[m.id] = url;
          }
        }
      }

      if (cancelled) {
        createdAudioUrls.forEach((u) => URL.revokeObjectURL(u));
        return;
      }

      setDecodedTextByMessage(nextDecodedText);
      setDecryptedAudioUrlByMessage((prev) => {
        Object.values(prev).forEach((u) => {
          if (!nextAudioUrls || !Object.values(nextAudioUrls).includes(u)) {
            URL.revokeObjectURL(u);
          }
        });
        return nextAudioUrls;
      });
    }

    void decodeEncryptedMessages();

    return () => {
      cancelled = true;
    };
  }, [messages]);

  let hasInsertedUnreadDivider = false;

  const participantCount = useMemo(() => {
    const ids = new Set<string>();
    if (userId) {
      ids.add(userId);
    }
    messages.forEach((m) => {
      if (m.senderId) {
        ids.add(m.senderId);
      }
    });
    typingUserIds.forEach((id) => {
      if (id) {
        ids.add(id);
      }
    });
    return ids.size;
  }, [messages, typingUserIds, userId]);

  const headerMemberCount = members.length > 0 ? members.length : participantCount;
  const chatHeaderReady = chatHeaderReadyForId === conversationId;

  const handleComposerModeChange = (mode: string | number | undefined) => {
    if (mode !== 'plain' && mode !== 'ghost' && mode !== 'coded') return;
    if (mode !== 'plain' && (isTyping || localTypingTimeoutRef.current !== null)) {
      if (localTypingTimeoutRef.current !== null) {
        window.clearTimeout(localTypingTimeoutRef.current);
        localTypingTimeoutRef.current = null;
      }
      setIsTyping(false);
      sendTypingEvent(false);
    }
    setComposerMode(mode);
  };

  const handleShareCodedUnlock = async (messageId: string, unlockKeyArg?: string) => {
    if (shareUnlockInProgress) return;
    try {
      setShareUnlockInProgress(true);
      const token = await getToken();
      const unlockKey =
        unlockKeyArg?.trim() ||
        lookupCodedUnlock(messageId, revealCodedForMessage?.decodeKey) ||
        undefined;
      const res = await fetch(apiUrl(`/api/chat/messages/${messageId}/share-unlock`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...apiHeaders(token),
        },
        body: JSON.stringify(unlockKey ? { unlockKey } : {}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          (body && (body.message || body.error)) ||
            `Failed to share unlock (${res.status})`
        );
      }
      setRevealCodedForMessage(null);
      const unlockedAt = new Date().toISOString();
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, unlockedAt: m.unlockedAt || unlockedAt }
            : m
        )
      );
      pushLiveAvailabilityNotice(
        'unlock',
        messageId,
        unlockedAt,
        t('chat.sender.you')
      );
      setToastMessage(t('chat.menu.feedback.unlocked'));
      requestScrollToBottom();
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : t('chat.error.unlockShareFailed')
      );
    } finally {
      setShareUnlockInProgress(false);
    }
  };

  const openComposerOptions = () => {
    setIsKeyPopoverOpen(true);
  };

  const getComposerPlaceholder = () => {
    if (isRecordingAudio) {
      return `${t('chat.audio.recording')} ${formatAudioTime(recordingSeconds)} / 5:00`;
    }
    if (composerMode === 'coded') return t('chat.input.placeholder.encoded');
    if (composerMode === 'ghost') return t('chat.input.placeholder.ghost');
    return t('chat.input.placeholder.plain');
  };

  const getComposerModeLabel = () => {
    if (composerMode === 'coded') return t('chat.composer.mode.coded');
    if (composerMode === 'ghost') return t('chat.composer.mode.ghost');
    return t('chat.composer.mode.plain');
  };

  const composerOptionsButton = (
    <IonButton
      id="composer-options-trigger"
      fill="solid"
      className={`chat-input-options chat-input-options--${composerMode}`}
      onClick={openComposerOptions}
      aria-label={t('chat.composer.optionsAria')}
    >
      <span className="chat-input-send-inner">
        <IonIcon icon={keyOutline} />
      </span>
    </IonButton>
  );

  return (
    <IonPage className="chat-page">
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/chats" text={t('common.actions.back')} />
          </IonButtons>
          <IonTitle aria-busy={!chatHeaderReady}>
            {chatHeaderReady ? (
              <span
                className="chat-title-pressable"
                onMouseDown={startTitleLongPress}
                onMouseUp={cancelTitleLongPress}
                onMouseLeave={cancelTitleLongPress}
                onTouchStart={startTitleLongPress}
                onTouchEnd={cancelTitleLongPress}
                onTouchCancel={cancelTitleLongPress}
              >
                <span className="chat-toolbar-title-inner">
                  <span
                    className="chat-header-meta chat-header-meta--balance"
                    aria-hidden="true"
                  >
                    <span className="chat-header-count" aria-hidden="true">
                      {headerMemberCount}
                    </span>
                    <IonIcon icon={peopleOutline} aria-hidden="true" />
                  </span>
                  <span className="chat-header-name">
                    {conversationName.trim() || t('chat.title')}
                  </span>
                  <span
                    className="chat-header-meta"
                    aria-label={t('chat.header.membersAria', {
                      count: headerMemberCount,
                    })}
                  >
                    <span className="chat-header-count" aria-hidden="true">
                      {headerMemberCount}
                    </span>
                    <IonIcon icon={peopleOutline} aria-hidden="true" />
                  </span>
                </span>
              </span>
            ) : null}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={() => setShowShareSheet(true)}
              aria-label={t('deepLink.chat.shareAria')}
            >
              <IonIcon slot="icon-only" icon={shareOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent
        ref={contentRef}
        className={`chat-content vault-screen ${patternVisible ? 'show-diagonal-pattern' : ''}`}
        scrollEvents={true}
        onIonScroll={handleScroll}
        fixedSlotPlacement="before"
      >
        {patternVisible && (
          <div className="chat-diagonal-pattern" slot="fixed" aria-hidden="true">
            <div className="chat-diagonal-pattern-layer">
              {Array.from({ length: 14 }).map((_, idx) => {
                const lineText = diagonalPatternLines[idx % diagonalPatternLines.length];
                return (
                  <div key={`pattern-${idx}`} className="chat-diagonal-pattern-line">
                    {diagonalLineContent(lineText)}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {typingLabel ? (
          <div className="typing-indicator" slot="fixed" aria-live="polite">
            <small>{typingLabel}</small>
          </div>
        ) : null}
        {initialLoading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && !initialLoading && (
          <div className="ion-padding">
            <IonText color="danger">
              <p>{error}</p>
            </IonText>
          </div>
        )}

        {!initialLoading && nextCursor && (
          <div className="ion-text-center ion-padding-top">
            <IonButton size="small" fill="clear" onClick={handleLoadMore}>
              {loadingMore ? (
                <IonSpinner name="crescent" />
              ) : (
                t('chat.loadOlder')
              )}
            </IonButton>
          </div>
        )}

        <div ref={listRef}>
          <IonList className="vault-feed-list">
            {messages.map((m, index) => {
              const isMe =
                (!!userId && m.senderId === userId) ||
                (!!userId && m.sender?.id === userId);
              const senderLabel = getSenderLabel(m);
              const prev = messages[index - 1];
              const isFirstOfGroup =
                !prev || prev.senderId !== m.senderId;

              const isUnread =
                !!readState?.lastReadAt &&
                toTimeMs(getMessageAvailableAt(m)) >
                  toTimeMs(readState.lastReadAt);
              const showUnreadDivider = !hasInsertedUnreadDivider && isUnread;
              if (showUnreadDivider) {
                hasInsertedUnreadDivider = true;
              }

              const sharedKey = parseSharedKey(m.content);
              const isMine =
                (!!userId && m.senderId === userId) ||
                (!!userId && m.sender?.id === userId);
              const isHiddenUnrevealed = !!m.isHidden && !m.revealedAt;
              const shouldShowGhostPlaceholder = isHiddenUnrevealed && !isMine;
              const isEncryptedText = isEncryptedTextContent(m.content);
              const decodedText = decodedTextByMessage[m.id];
              const isLockedEncoded = isEncryptedText && !decodedText;
              const availableAt = getMessageAvailableAt(m);
              const isJustAvailable =
                liveAvailabilityNotices.some((e) => e.messageId === m.id) ||
                (!!readState?.lastReadAt &&
                  !shouldShowGhostPlaceholder &&
                  toTimeMs(availableAt) > toTimeMs(readState.lastReadAt) &&
                  (toTimeMs(m.revealedAt) === toTimeMs(availableAt) ||
                    toTimeMs(m.unlockedAt) === toTimeMs(availableAt)));
              const justAvailableKind = liveAvailabilityNotices.find(
                (e) => e.messageId === m.id
              )?.kind;
              const justAvailableLabel =
                justAvailableKind === 'unlock' ||
                (toTimeMs(m.unlockedAt) === toTimeMs(availableAt) && !!m.unlockedAt)
                  ? t('chat.activity.justRevealed')
                  : t('chat.activity.justUnghosted');
              const isAudioMessage = m.type === 'AUDIO' && m.attachments.length > 0;
              const audioAttachment = isAudioMessage ? m.attachments[0] : null;
              const isEncryptedAudio = isEncryptedAudioContent(m.content);
              const audioSrc = isEncryptedAudio
                ? decryptedAudioUrlByMessage[m.id] || null
                : audioAttachment?.url || null;
              const audioState = audioPlaybackByMessage[m.id] || {
                playing: false,
                currentTime: 0,
                duration: audioAttachment?.durationSec || 0,
              };

              return (
                <React.Fragment key={m.id}>
                  {showUnreadDivider && (
                    <div className="unread-divider">
                      <span>{t('chat.unreadDivider')}</span>
                    </div>
                  )}
                  <IonItem
                    lines="none"
                    button={false}
                    className={`${isMe ? 'message-row me' : 'message-row other'}${
                      highlightMessageId === m.id ? ' message-row--highlight' : ''
                    }`}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setMessageMenuFor(m);
                    }}
                    onDoubleClick={() => {
                      setMessageMenuFor(m);
                    }}
                    onMouseDown={() => startMessageLongPress(m)}
                    onMouseUp={cancelMessageLongPress}
                    onMouseLeave={cancelMessageLongPress}
                    onTouchStart={() => startMessageLongPress(m)}
                    onTouchEnd={cancelMessageLongPress}
                    onTouchCancel={cancelMessageLongPress}
                  >
                    {!isMe && isFirstOfGroup && (
                      <div className="message-avatar">
                        <span>{senderLabel.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                    <div
                      className={`message-content${
                        highlightMessageId === m.id
                          ? ' message-content--highlight'
                          : ''
                      }`}
                      id={`msg-${m.id}`}
                    >
                      {isFirstOfGroup && (
                        <p
                          className={
                            isMe ? 'message-sender me' : 'message-sender other'
                          }
                        >
                          {senderLabel}
                        </p>
                      )}
                      <div className="message-row-actions">
                        <IonButton
                          fill="clear"
                          size="small"
                          onClick={() => setMessageMenuFor(m)}
                          aria-label={t('chat.menu.open')}
                        >
                          <IonIcon icon={ellipsisVertical} />
                        </IonButton>
                      </div>
                      {renderReplyQuote(m)}
                      {shouldShowGhostPlaceholder ? (
                        <div className="message-bubble ghost-hidden-bubble">
                          <p className="message-text">
                            {t('chat.ghost.hidden.other')}
                          </p>
                          <p className="message-meta">
                            <small>
                              <span className="ghost-badge">
                                {t('chat.ghost.badge')}
                              </span>
                              &nbsp;·&nbsp;
                              {new Date(m.createdAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </small>
                          </p>
                        </div>
                      ) : sharedKey ? (
                        <div className="message-bubble reveal-card reveal-card-compact">
                          <p className="message-text">
                            {t('chat.key.shared.compact')}
                          </p>
                          <p className="message-meta">
                            <small>
                              <span className="vault-chip vault-chip--celebratory">
                                {t('chat.key.badge')}
                              </span>
                              &nbsp;·&nbsp;
                              {new Date(m.createdAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </small>
                          </p>
                        </div>
                      ) : isAudioMessage && audioAttachment ? (
                        <div className="message-bubble audio-message-bubble">
                          {audioSrc && (
                          <audio
                            ref={(el) => {
                              audioElementsRef.current[m.id] = el;
                            }}
                            src={audioSrc}
                            preload="metadata"
                            onLoadedMetadata={(e) => {
                              const media = e.currentTarget;
                              setAudioPlaybackByMessage((prev) => ({
                                ...prev,
                                [m.id]: {
                                  playing: !media.paused,
                                  currentTime: media.currentTime,
                                  duration:
                                    Number.isFinite(media.duration) && media.duration > 0
                                      ? media.duration
                                      : audioAttachment.durationSec || 0,
                                },
                              }));
                            }}
                            onTimeUpdate={(e) => {
                              const media = e.currentTarget;
                              setAudioPlaybackByMessage((prev) => ({
                                ...prev,
                                [m.id]: {
                                  playing: !media.paused,
                                  currentTime: media.currentTime,
                                  duration:
                                    Number.isFinite(media.duration) && media.duration > 0
                                      ? media.duration
                                      : prev[m.id]?.duration || audioAttachment.durationSec || 0,
                                },
                              }));
                            }}
                            onPlay={() => {
                              setAudioPlaybackByMessage((prev) => ({
                                ...prev,
                                [m.id]: {
                                  ...(prev[m.id] || { currentTime: 0, duration: 0 }),
                                  playing: true,
                                },
                              }));
                            }}
                            onPause={() => {
                              setAudioPlaybackByMessage((prev) => ({
                                ...prev,
                                [m.id]: {
                                  ...(prev[m.id] || { currentTime: 0, duration: 0 }),
                                  playing: false,
                                },
                              }));
                            }}
                          />
                          )}
                          <div className="audio-controls-row">
                            <IonButton
                              size="small"
                              fill="outline"
                              onClick={() => toggleAudioPlayback(m.id)}
                              disabled={!audioSrc}
                            >
                              <IonIcon icon={audioState.playing ? pause : play} />
                            </IonButton>
                            <input
                              className="audio-progress-input"
                              type="range"
                              min={0}
                              max={
                                audioState.duration > 0
                                  ? audioState.duration
                                  : audioAttachment.durationSec || 1
                              }
                              step={0.1}
                              value={audioState.currentTime}
                              onChange={(e) => {
                                const next = Number(e.currentTarget.value);
                                const el = audioElementsRef.current[m.id];
                                if (el) {
                                  el.currentTime = next;
                                }
                                setAudioPlaybackByMessage((prev) => ({
                                  ...prev,
                                  [m.id]: {
                                    ...(prev[m.id] || {
                                      playing: false,
                                      duration: audioAttachment.durationSec || 0,
                                    }),
                                    currentTime: next,
                                  },
                                }));
                              }}
                            />
                          </div>
                          {isEncryptedAudio && !audioSrc && !isMe && (
                            <IonText color="medium">
                              <small>{t('chat.message.encodedBadge')}</small>
                            </IonText>
                          )}
                          <p className="message-meta">
                            <small>
                              {isEncryptedAudio && (
                                <span className="encoded-badge">
                                  {audioSrc ? t('chat.label.uncoded') : t('chat.label.coded')}
                                </span>
                              )}
                              {m.isHidden && (
                                <span className="ghost-badge">
                                  {m.revealedAt ? t('chat.label.unghost') : t('chat.label.ghost')}
                                </span>
                              )}
                              {isJustAvailable && (
                                <span className="availability-badge">
                                  {justAvailableLabel}
                                </span>
                              )}
                              &nbsp;
                              {formatAudioTime(audioState.currentTime)} /{' '}
                              {formatAudioTime(
                                audioState.duration || audioAttachment.durationSec || 0
                              )}
                            </small>
                          </p>
                        </div>
                      ) : (
                        <div
                          className={`message-bubble encoded-transition ${
                            isLockedEncoded ? 'ghost-message' : 'is-revealed'
                          } ${isHiddenUnrevealed ? 'ghost-hidden-bubble' : ''}`}
                        >
                          <p className={`message-text ${isLockedEncoded ? 'is-locked' : ''}`}>
                            {decodedText || (isEncryptedText ? t('chat.message.encodedBadge') : m.content) || t('chat.message.deleted')}
                          </p>
                          <p className="message-meta">
                            <small>
                              {isEncryptedText && (
                                <span className="encoded-badge">
                                  {isLockedEncoded ? t('chat.label.coded') : t('chat.label.uncoded')}
                                </span>
                              )}
                              {m.isHidden && (
                                <>
                                  <span className="ghost-badge">
                                    {m.revealedAt ? t('chat.label.unghost') : t('chat.label.ghost')}
                                  </span>
                                </>
                              )}
                              {isJustAvailable && (
                                <span className="availability-badge">
                                  {justAvailableLabel}
                                </span>
                              )}
                              {new Date(m.createdAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </small>
                          </p>
                        </div>
                      )}
                    </div>
                  </IonItem>
                </React.Fragment>
              );
            })}
            {availabilityActivities.length > 0 && (
              <div className="availability-activity-list" role="list">
                {availabilityActivities.map((ev) => (
                  <div
                    key={ev.id}
                    className="availability-activity"
                    role="listitem"
                  >
                    <button
                      type="button"
                      className="availability-activity-main"
                      onClick={() => scrollToMessageAndHighlight(ev.messageId)}
                    >
                      <span className="availability-activity-text">
                        {ev.kind === 'unghost'
                          ? t('chat.activity.unghosted', {
                              name: ev.actorLabel,
                            })
                          : t('chat.activity.revealed', {
                              name: ev.actorLabel,
                            })}
                      </span>
                      <span className="availability-activity-time">
                        {new Date(ev.at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </button>
                    <IonButton
                      fill="clear"
                      size="small"
                      className="availability-activity-dismiss"
                      aria-label={t('chat.activity.dismissAria')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDismissAvailabilityTarget(ev);
                      }}
                    >
                      <IonIcon slot="icon-only" icon={closeOutline} />
                    </IonButton>
                  </div>
                ))}
              </div>
            )}
          </IonList>
        </div>

        <style>{`
          .chat-content {
            --padding-start: 12px;
            --padding-end: 12px;
            --padding-top: 8px;
            --padding-bottom: 8px;
            position: relative;
            --background: ${CHAT_BG};
            background: ${CHAT_BG};
          }

          .chat-page {
            background: ${CHAT_BG};
          }

          .chat-page ion-content {
            --background: ${CHAT_BG};
          }
          /* slot="fixed": not inside scroll — avoids 100vh abs overflow inflating scrollHeight */
          .chat-diagonal-pattern {
            container-type: size;
            pointer-events: none;
            z-index: 0;
            overflow: hidden;
            left: 0;
            right: 0;
            /* Same vertical extent as ion-content .inner-scroll (translucent header / footer) */
            top: calc(var(--offset-top, 0px) * -1);
            bottom: calc(var(--offset-bottom, 0px) * -1);
          }
          .chat-diagonal-pattern-layer {
            position: absolute;
            left: 50%;
            top: 50%;
            width: max(
              calc(100cqmax / (cos(30deg) + sin(30deg)) * 1.08),
              calc(100vmax / (cos(30deg) + sin(30deg)) * 1.08)
            );
            height: max(
              calc(100cqmax / (cos(30deg) + sin(30deg)) * 1.08),
              calc(100vmax / (cos(30deg) + sin(30deg)) * 1.08)
            );
            overflow: hidden;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: 0;
            transform: translate(-50%, -50%) rotate(-30deg);
            opacity: 0.24;
          }
          .chat-diagonal-pattern-line {
            white-space: nowrap;
            font-size: 19px;
            letter-spacing: 0.32em;
            text-transform: uppercase;
            color: rgba(var(--ion-text-color-rgb), 0.2);
            font-weight: 800;
          }

          @media (min-width: 1024px) {
            .chat-diagonal-pattern-line {
              font-size: 24px;
              letter-spacing: 0.36em;
            }
          }
          .chat-content.show-diagonal-pattern .vault-feed-list {
            position: relative;
            z-index: 2;
          }

          /* ion-list / ion-item use overflow:hidden on host — clips ghost border + drop-shadow */
          .chat-content ion-list.vault-feed-list {
            overflow: visible;
          }
          .chat-content ion-item.message-row {
            -webkit-touch-callout: none;
            overflow: visible;
          }

          .vault-glass-popover {
            --width: clamp(280px, 86vw, 340px);
          }

          .composer-options-popover {
            --width: clamp(280px, 92vw, 360px);
          }

          /* Remove default list/item backgrounds in chat */
          .chat-content ion-list {
            background: transparent !important;
          }

          .chat-content ion-item {
            --background: transparent;
            --background-focused: transparent;
            --background-activated: transparent;
          }

          .message-row.me,
          .message-row.other {
            --padding-start: 0;
            --inner-padding-end: 0;
            --min-height: auto;
            margin-bottom: var(--vault-space-3);
            display: flex;
            align-items: flex-start;
          }

          .message-row.other {
            justify-content: flex-start;
            position: relative;
          }

          .message-row.me {
            justify-content: flex-end;
          }

          .message-content {
            max-width: 80%;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 2px;
            position: relative;
            box-sizing: border-box;
          }
          /* global .encoded-transition.is-revealed uses scale(1.02) — overflows ~3px past ion-item */
          .message-content .message-bubble.encoded-transition.is-revealed {
            transform: none;
          }
          .message-row-actions {
            position: absolute;
            top: -6px;
            right: -34px;
            z-index: 2;
          }
          .message-row-actions ion-button {
            --padding-start: 0;
            --padding-end: 0;
            margin: 0;
            width: 28px;
            height: 28px;
            min-height: 28px;
            opacity: 0.72;
          }

          /* Align current user messages to the right, others to the left */
          .message-row.me .message-content {
            margin-left: auto;
            align-items: flex-end;
          }

          .message-row.other .message-content {
            align-items: flex-start;
          }

          .message-avatar {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: var(--vault-surface-high);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: 500;
            color: var(--ion-text-color);
          }
          .message-row.other .message-avatar {
            position: absolute;
            left: -38px;
            bottom: 8px;
          }

          .message-sender {
            margin: 0 4px;
            font-size: 11px;
            font-weight: 500;
          }

          .message-sender.other {
            color: rgba(var(--ion-text-color-rgb), 0.8);
          }

          .message-sender.me {
            text-align: right;
            color: rgba(var(--ion-text-color-rgb), 0.92);
          }

          .message-bubble {
            padding: 10px 12px;
            border-radius: 16px;
            background: var(--vault-surface-low);
            color: var(--ion-text-color);
            margin: 2px 0;
          }

          .message-row.other .message-bubble {
            background: var(--vault-surface-low);
            color: var(--ion-text-color);
          }

          .message-row.me .message-bubble {
            background: var(--ion-color-primary);
            color: var(--ion-color-primary-contrast);
          }

          .message-text {
            margin: 0;
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
          }

          .message-meta {
            margin-top: 2px;
            text-align: right;
            font-size: 11px;
            color: rgba(var(--ion-text-color-rgb), 0.7);
            margin-bottom: 2px;
          }

          .encoded-badge {
            display: inline-block;
            padding: 2px 8px;
            margin-right: 8px;
            border-radius: 999px;
            background: rgba(var(--ion-color-primary-rgb), 0.15);
            color: var(--ion-color-primary);
            border: 1px solid rgba(var(--ion-color-primary-rgb), 0.5);
            font-size: 10px;
            font-weight: 700;
            text-transform: none;
            filter: drop-shadow(0 0 8px #c180ff33);
          }

          .message-row.me .encoded-badge {
            background: rgba(255, 255, 255, 0.22);
            color: var(--ion-color-primary-contrast);
            border: 1px solid rgba(255, 255, 255, 0.35);
            filter: none;
          }

          .unread-divider {
            text-align: center;
            margin: 8px 0;
          }

          .unread-divider span {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 999px;
            background: rgba(var(--ion-color-primary-rgb), 0.15);
            color: var(--ion-color-primary);
            font-size: 11px;
            font-weight: 500;
          }

          .availability-activity-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 10px 4px 16px;
          }

          .availability-activity {
            display: flex;
            align-items: center;
            gap: 2px;
            width: 100%;
            margin: 0;
            padding: 4px 4px 4px 14px;
            border-radius: 12px;
            border: 1px solid rgba(var(--ion-color-primary-rgb), 0.28);
            background: rgba(var(--ion-color-primary-rgb), 0.1);
            color: rgba(255, 255, 255, 0.92);
          }

          .availability-activity-main {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            flex: 1;
            min-width: 0;
            margin: 0;
            padding: 6px 0;
            border: 0;
            background: transparent;
            color: inherit;
            text-align: left;
            cursor: pointer;
            font: inherit;
          }

          .availability-activity-main:active {
            opacity: 0.85;
          }

          .availability-activity-text {
            font-size: 13px;
            line-height: 1.35;
          }

          .availability-activity-time {
            flex-shrink: 0;
            font-size: 11px;
            opacity: 0.7;
          }

          .availability-activity-dismiss {
            flex-shrink: 0;
            margin: 0;
            --padding-start: 6px;
            --padding-end: 6px;
            height: 32px;
          }

          .availability-badge {
            display: inline-block;
            margin-right: 6px;
            padding: 1px 6px;
            border-radius: 999px;
            background: rgba(var(--ion-color-warning-rgb, 255, 196, 9), 0.2);
            color: var(--ion-color-warning, #ffc409);
            border: 1px solid rgba(var(--ion-color-warning-rgb, 255, 196, 9), 0.45);
            font-size: 10px;
            font-weight: 700;
            text-transform: none;
          }

          .message-content--highlight .message-bubble,
          .message-row--highlight .message-bubble {
            animation: message-availability-pulse 1.6s ease;
          }

          @keyframes message-availability-pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(var(--ion-color-primary-rgb), 0.55);
            }
            40% {
              box-shadow: 0 0 0 8px rgba(var(--ion-color-primary-rgb), 0.18);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(var(--ion-color-primary-rgb), 0);
            }
          }

          .chat-input-send {
            margin: 0;
            flex-shrink: 0;
            height: 36px;
            min-height: 36px;
            min-width: 36px;
            align-self: center;
            --padding-start: 8px;
            --padding-end: 8px;
          }

          .chat-footer,
          .chat-footer::before {
            --background: transparent !important;
            background: transparent !important;
            border: 0 !important;
            box-shadow: none !important;
          }

          .chat-footer ion-toolbar {
            --background: transparent !important;
            --border-width: 0;
            --min-height: 0;
            --padding-top: 6px;
            --padding-bottom: 6px;
            --padding-start: 8px;
            --padding-end: 8px;
            background: transparent !important;
            border: 0 !important;
            box-shadow: none !important;
          }

          .chat-footer ion-toolbar::part(container),
          .chat-footer ion-toolbar::part(native) {
            background: transparent !important;
            border: 0 !important;
            box-shadow: none !important;
          }

          .chat-footer,
          .chat-footer ion-toolbar,
          .chat-footer ion-toolbar::part(container),
          .chat-footer ion-toolbar::part(native) {
            background-image: none !important;
          }

          .chat-input-options {
            margin: 0;
            margin-inline-end: 6px;
            flex-shrink: 0;
            height: 36px;
            min-height: 36px;
            min-width: 36px;
            align-self: center;
            --padding-start: 8px;
            --padding-end: 8px;
            --border-radius: 10px;
          }

          .chat-input-options ion-icon {
            font-size: 22px;
          }

          .chat-input-send-inner {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .chat-input-send ion-icon {
            font-size: 22px;
            color: var(--ion-color-primary-contrast);
          }

          .typing-indicator {
            left: 16px;
            right: 16px;
            /* Sit just above the composer without carving a strip out of the footer */
            bottom: calc(var(--offset-bottom, 0px) + 6px);
            z-index: 3;
            pointer-events: none;
            padding: 0;
            margin: 0;
            min-height: 0;
            height: auto;
            display: block;
            font-size: 12px;
            line-height: 1.3;
            color: rgba(222, 229, 255, 0.82);
            text-shadow: 0 1px 3px rgba(4, 10, 24, 0.85);
            background: transparent;
          }

          .audio-message-bubble audio {
            width: 100%;
            margin-bottom: 6px;
          }

          .audio-controls-row {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .message-row.me .audio-controls-row ion-button {
            --border-color: rgba(255, 255, 255, 0.8);
            --color: var(--ion-color-primary-contrast);
            --background: rgba(255, 255, 255, 0.18);
          }

          .message-row.me .audio-controls-row ion-button ion-icon {
            color: var(--ion-color-primary-contrast);
          }

          .audio-progress-input {
            width: 100%;
          }

          .encode-popover {
            padding: 12px 14px;
            width: 100%;
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;
          }

          .encode-popover ion-item {
            --padding-start: 0;
            --padding-end: 0;
            --inner-padding-start: 0;
            --inner-padding-end: 0;
          }

          .encode-popover-buttons {
            display: flex;
            justify-content: center;
            gap: 8px;
            flex-wrap: wrap;
            width: 100%;
          }

          .encode-popover-buttons ion-button {
            margin-inline: 0;
            flex: 1 1 calc(50% - 4px);
            min-width: 0;
            max-width: 100%;
          }

          .encode-popover ion-item {
            width: 100%;
            box-sizing: border-box;
            --padding-start: 0;
            --padding-end: 0;
            --inner-padding-start: 0;
            --inner-padding-end: 0;
          }

          .encode-popover-center {
            flex: 1;
            display: flex;
            justify-content: center;
            text-align: center;
            width: 100%;
          }

          .encode-popover-center ion-button {
            width: 100%;
          }

          .composer-mode-segment-item {
            --padding-start: 0;
            --padding-end: 0;
            --inner-padding-start: 0;
            --inner-padding-end: 0;
            --min-height: auto;
            align-items: center;
          }

          .composer-mode-segment-wrap {
            width: 100%;
            padding: 0 0 4px;
            box-sizing: border-box;
          }

          .composer-mode-segment-wrap ion-segment {
            width: 100%;
          }

          .composer-mode-segment {
            border-radius: 10px;
          }

          .composer-mode-segment ion-segment-button {
            --padding-start: 4px;
            --padding-end: 4px;
            --color: rgba(222, 229, 255, 0.88);
            min-width: 0;
          }

          .composer-mode-segment ion-segment-button ion-label {
            color: rgba(222, 229, 255, 0.88);
            transition: color 0.22s ease;
          }

          .chat-title-pressable {
            display: inline-block;
            max-width: 100%;
            cursor: pointer;
            user-select: none;
          }

          .chat-toolbar-title-inner {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            max-width: min(100%, 72vw);
          }

          .chat-header-name {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            min-width: 0;
            font-weight: 600;
          }

          .chat-header-meta {
            display: inline-flex;
            align-items: center;
            gap: 2px;
            flex-shrink: 0;
            color: var(--ion-color-medium, #92949c);
            font-weight: 500;
            font-size: 0.9em;
          }

          /* Invisible duplicate of member meta: keeps title optically centered vs. visible right meta */
          .chat-header-meta--balance {
            visibility: hidden;
            pointer-events: none;
            user-select: none;
          }

          .chat-header-meta ion-icon {
            font-size: 1.15rem;
          }

          .ghost-hidden-bubble {
            border: 1px dashed rgba(var(--ion-color-primary-rgb), 0.5);
            background: rgba(var(--ion-color-primary-rgb), 0.08);
            color: var(--ion-text-color);
          }

          .message-row.me .ghost-hidden-bubble {
            background: rgba(var(--ion-color-primary-rgb), 0.12);
            color: var(--ion-text-color);
          }

          .ghost-hidden-bubble .message-meta {
            color: rgba(var(--ion-text-color-rgb), 0.9);
          }

          .ghost-badge {
            display: inline-block;
            padding: 2px 8px;
            margin-right: 8px;
            border-radius: 999px;
            background: rgba(var(--ion-color-primary-rgb), 0.2);
            color: var(--ion-color-primary);
            border: 1px solid rgba(var(--ion-color-primary-rgb), 0.55);
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.02em;
            text-transform: none;
          }

          .message-row.me .ghost-badge {
            background: rgba(255, 255, 255, 0.2);
            color: var(--ion-color-primary-contrast);
            border: 1px solid rgba(255, 255, 255, 0.45);
          }

          .reveal-card-compact {
            padding: 6px 10px;
            border-radius: 12px;
          }

          .reveal-card-compact .message-text {
            font-size: 12px;
            font-weight: 600;
          }

          .reveal-card-compact .message-meta {
            margin-top: 0;
            font-size: 10px;
          }

          .message-reply-quote {
            display: flex;
            gap: 8px;
            align-items: stretch;
            margin-bottom: 6px;
            max-width: 100%;
          }

          .message-reply-quote--clickable {
            cursor: pointer;
            border-radius: 10px;
            margin-left: -4px;
            margin-right: -4px;
            padding: 4px;
            transition: background 0.15s ease;
          }

          .message-reply-quote--clickable:hover {
            background: rgba(var(--ion-color-medium-rgb), 0.12);
          }

          .message-reply-quote--clickable:focus-visible {
            outline: 2px solid var(--ion-color-primary, #3880ff);
            outline-offset: 2px;
          }

          .message-content--highlight {
            animation: chat-reply-highlight-pulse 1.4s ease-out;
          }

          @keyframes chat-reply-highlight-pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(var(--ion-color-primary-rgb), 0.55);
            }
            35% {
              box-shadow: 0 0 0 6px rgba(var(--ion-color-primary-rgb), 0.2);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(var(--ion-color-primary-rgb), 0);
            }
          }

          .message-reply-quote-bar {
            width: 3px;
            flex-shrink: 0;
            border-radius: 2px;
            background: rgba(var(--ion-color-primary-rgb), 0.55);
          }

          .message-row.me .message-reply-quote-bar {
            background: rgba(255, 255, 255, 0.55);
          }

          .message-reply-quote-inner {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .message-reply-quote-author {
            font-size: 12px;
            font-weight: 600;
            opacity: 0.95;
          }

          .message-reply-quote-snippet {
            font-size: 12px;
            color: rgba(var(--ion-text-color-rgb), 0.72);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .chat-composer-panel {
            margin: 0 8px 10px;
            border-radius: 14px;
            border: 1px solid rgba(var(--ion-color-medium-rgb), 0.35);
            overflow: hidden;
            background: var(--ion-background-color, #fff);
          }

          .chat-reply-compose-bar {
            display: flex;
            align-items: flex-start;
            gap: 4px;
            padding: 8px 10px;
            border-bottom: 1px solid rgba(var(--ion-color-medium-rgb), 0.28);
            background: rgba(var(--ion-color-medium-rgb), 0.09);
          }

          .chat-reply-compose-text {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .chat-reply-compose-label {
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: var(--ion-color-medium, #92949c);
          }

          .chat-reply-compose-name {
            font-size: 13px;
            font-weight: 600;
          }

          .chat-reply-compose-snippet {
            font-size: 13px;
            color: var(--ion-color-medium, #92949c);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .chat-reply-compose-dismiss {
            flex-shrink: 0;
            margin: -4px -6px 0 0;
          }

          .chat-composer-panel .chat-input-item {
            --inner-padding-end: 0;
            --inner-padding-start: 0;
          }
        `}</style>
      </IonContent>
      <IonFooter className="chat-footer">
        <IonToolbar>
          {replyingTo ? (
            <div className="chat-composer-panel">
              <div className="chat-reply-compose-bar">
                <div className="chat-reply-compose-text">
                  <span className="chat-reply-compose-label">
                    {t('chat.reply.composingTo')}
                  </span>
                  <span className="chat-reply-compose-name">
                    {getSenderLabel(replyingTo)}
                  </span>
                  <span className="chat-reply-compose-snippet">
                    {getReplySnippetForCompose(replyingTo)}
                  </span>
                </div>
                <IonButton
                  fill="clear"
                  size="small"
                  className="chat-reply-compose-dismiss"
                  onClick={() => setReplyingTo(null)}
                  aria-label={t('chat.reply.dismiss')}
                >
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </div>
              {audioValidationError && (
                <IonItem lines="none" className="chat-input-item">
                  <IonText color="danger">
                    <small>{audioValidationError}</small>
                  </IonText>
                </IonItem>
              )}
              {recordedAudioBlob && recordedAudioUrl && (
                <IonItem lines="none" className="chat-input-item">
                  <div style={{ width: '100%' }}>
                    <audio controls src={recordedAudioUrl} style={{ width: '100%' }} />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '6px',
                      }}
                    >
                      <IonText color="medium">
                        <small>
                          {t('chat.audio.preview')}: {formatAudioTime(recordedAudioDurationSec)}
                        </small>
                      </IonText>
                      <IonButton
                        size="small"
                        color="danger"
                        fill="clear"
                        onClick={discardRecordedAudio}
                        disabled={sending}
                      >
                        <IonIcon icon={trash} slot="start" />
                        {t('chat.audio.discard')}
                      </IonButton>
                    </div>
                  </div>
                </IonItem>
              )}
              <div className="chat-input-item">
                {composerOptionsButton}
                <input
                  className="chat-input-field"
                  type="text"
                  value={input}
                  placeholder={getComposerPlaceholder()}
                  inputMode="text"
                  enterKeyHint="send"
                  readOnly={isRecordingAudio}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                <IonButton
                  className="chat-input-send"
                  onClick={() => {
                    if (input.trim()) {
                      handleSend();
                      return;
                    }
                    if (recordedAudioBlob) {
                      handleSendAudio();
                      return;
                    }
                    if (!isRecordingAudio) {
                      void startRecording();
                    } else {
                      stopRecording();
                    }
                  }}
                  disabled={sending}
                  aria-label={getComposerModeLabel()}
                >
                  <span className="chat-input-send-inner">
                    <IonIcon
                      icon={
                        input.trim() || recordedAudioBlob
                          ? send
                          : isRecordingAudio
                          ? square
                          : mic
                      }
                    />
                  </span>
                </IonButton>
              </div>
            </div>
          ) : (
            <>
              {audioValidationError && (
                <IonItem lines="none" className="chat-input-item">
                  <IonText color="danger">
                    <small>{audioValidationError}</small>
                  </IonText>
                </IonItem>
              )}
              {recordedAudioBlob && recordedAudioUrl && (
                <IonItem lines="none" className="chat-input-item">
                  <div style={{ width: '100%' }}>
                    <audio controls src={recordedAudioUrl} style={{ width: '100%' }} />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '6px',
                      }}
                    >
                      <IonText color="medium">
                        <small>
                          {t('chat.audio.preview')}: {formatAudioTime(recordedAudioDurationSec)}
                        </small>
                      </IonText>
                      <IonButton
                        size="small"
                        color="danger"
                        fill="clear"
                        onClick={discardRecordedAudio}
                        disabled={sending}
                      >
                        <IonIcon icon={trash} slot="start" />
                        {t('chat.audio.discard')}
                      </IonButton>
                    </div>
                  </div>
                </IonItem>
              )}
              <div className="chat-input-item">
                {composerOptionsButton}
                <input
                  className="chat-input-field"
                  type="text"
                  value={input}
                  placeholder={getComposerPlaceholder()}
                  inputMode="text"
                  enterKeyHint="send"
                  readOnly={isRecordingAudio}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                <IonButton
                  className="chat-input-send"
                  onClick={() => {
                    if (input.trim()) {
                      handleSend();
                      return;
                    }
                    if (recordedAudioBlob) {
                      handleSendAudio();
                      return;
                    }
                    if (!isRecordingAudio) {
                      void startRecording();
                    } else {
                      stopRecording();
                    }
                  }}
                  disabled={sending}
                  aria-label={getComposerModeLabel()}
                >
                  <span className="chat-input-send-inner">
                    <IonIcon
                      icon={
                        input.trim() || recordedAudioBlob
                          ? send
                          : isRecordingAudio
                          ? square
                          : mic
                      }
                    />
                  </span>
                </IonButton>
              </div>
            </>
          )}
        </IonToolbar>
      </IonFooter>
      <IonPopover
        className="vault-glass-popover composer-options-popover"
        keepContentsMounted={true}
        isOpen={isKeyPopoverOpen}
        dismissOnSelect={false}
        onDidDismiss={() => {
          setIsKeyPopoverOpen(false);
        }}
      >
        <IonList className="encode-popover">
          <IonItem lines="none">
            <div className="encode-popover-center">
              <IonText>
                <strong>{t('chat.composer.optionsTitle')}</strong>
              </IonText>
            </div>
          </IonItem>
          <IonItem lines="none" className="composer-mode-segment-item">
            <div className="composer-mode-segment-wrap">
              <IonSegment
                className={`composer-mode-segment composer-mode-segment--${composerMode}`}
                value={composerMode}
                onIonChange={(e) => handleComposerModeChange(e.detail.value)}
              >
                <IonSegmentButton value="plain">
                  <IonLabel>{t('chat.composer.mode.plain')}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="coded">
                  <IonLabel>{t('chat.composer.mode.coded')}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="ghost">
                  <IonLabel>{t('chat.composer.mode.ghost')}</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </div>
          </IonItem>
          <IonItem lines="none" style={{ display: 'none' }} aria-hidden="true">
            <IonText color="medium">
              <small>{t('chat.pattern.toggle')}</small>
            </IonText>
            <IonToggle
              slot="end"
              checked={showPattern}
              onIonChange={(e) => setShowPattern(e.detail.checked)}
            />
          </IonItem>
        </IonList>
      </IonPopover>
      <IonPopover
        className="vault-glass-popover"
        isOpen={!!revealCodedForMessage}
        onDidDismiss={() => {
          if (!shareUnlockInProgress) {
            setRevealCodedForMessage(null);
          }
        }}
      >
        <IonList className="encode-popover">
          <IonItem lines="none">
            <div className="encode-popover-center">
              <IonText>
                <strong>{t('chat.reveal.title')}</strong>
              </IonText>
            </div>
          </IonItem>
          <IonItem lines="none">
            <div className="encode-popover-center">
              <IonText color="medium">
                <small>{t('chat.reveal.description')}</small>
              </IonText>
            </div>
          </IonItem>
          <IonItem lines="none">
            <div className="encode-popover-center encode-popover-buttons">
              <IonButton
                expand="block"
                disabled={shareUnlockInProgress || !revealCodedForMessage}
                onClick={() => {
                  if (!revealCodedForMessage) return;
                  const msg = revealCodedForMessage;
                  const unlockKey = lookupCodedUnlock(msg.id, msg.decodeKey);
                  void handleShareCodedUnlock(msg.id, unlockKey || undefined);
                }}
              >
                {shareUnlockInProgress ? (
                  <IonSpinner name="crescent" />
                ) : (
                  t('chat.reveal.confirm')
                )}
              </IonButton>
              <IonButton
                fill="clear"
                color="medium"
                disabled={shareUnlockInProgress}
                onClick={() => setRevealCodedForMessage(null)}
              >
                {t('chat.reveal.cancel')}
              </IonButton>
            </div>
          </IonItem>
        </IonList>
      </IonPopover>
      <IonPopover
        className="vault-glass-popover"
        isOpen={!!messageMenuFor}
        onDidDismiss={() => setMessageMenuFor(null)}
      >
        <IonList className="encode-popover">
          {messageMenuFor && (
            <>
              {canOfferReplyToMessage(messageMenuFor) && (
                <IonItem
                  button
                  onClick={() => {
                    const msg = messageMenuFor;
                    setMessageMenuFor(null);
                    if (msg) setReplyingTo(msg);
                  }}
                >
                  <IonText>{t('chat.reply.action')}</IonText>
                </IonItem>
              )}
              {messageMenuFor.isHidden && !messageMenuFor.revealedAt && (
                <IonItem
                  button
                  onClick={() => {
                    const id = messageMenuFor.id;
                    setMessageMenuFor(null);
                    void handleRevealMessage(id);
                  }}
                >
                  <IonText>{t('chat.menu.unghost')}</IonText>
                </IonItem>
              )}
              {messageMenuFor.isHidden && !!messageMenuFor.revealedAt && (
                <IonItem disabled>
                  <IonText color="medium">{t('chat.menu.removeGhost')}</IonText>
                </IonItem>
              )}
              {(isEncryptedTextContent(messageMenuFor.content) ||
                isEncryptedAudioContent(messageMenuFor.content)) &&
              (messageMenuFor.senderId === userId ||
                (!!userId && messageMenuFor.sender?.id === userId)) ? (
                <IonItem
                  button
                  disabled={
                    !!messageMenuFor.isHidden && !messageMenuFor.revealedAt
                  }
                  onClick={() => {
                    const msg = messageMenuFor;
                    setMessageMenuFor(null);
                    if (!msg) return;
                    if (msg.isHidden && !msg.revealedAt) {
                      setToastMessage(t('chat.menu.unghostFirst'));
                      return;
                    }
                    openAfterMenuClose(() => {
                      const unlockKey = lookupCodedUnlock(msg.id, msg.decodeKey);
                      setRevealCodedForMessage(
                        unlockKey ? { ...msg, decodeKey: unlockKey } : msg
                      );
                    });
                  }}
                >
                  <IonText>{t('chat.menu.reveal')}</IonText>
                </IonItem>
              ) : null}
              {(messageMenuFor.senderId === userId ||
                (!!userId && messageMenuFor.sender?.id === userId)) && (
                <IonItem
                  button
                  disabled={deleteInProgressFor === messageMenuFor.id}
                  onClick={() => {
                    const id = messageMenuFor.id;
                    setMessageMenuFor(null);
                    void handleDeleteMessage(id);
                  }}
                >
                  <IonText color="danger">{t('chat.menu.delete')}</IonText>
                </IonItem>
              )}
            </>
          )}
        </IonList>
      </IonPopover>
      <IonPopover
        className="vault-glass-popover"
        isOpen={membersPopoverOpen}
        onDidDismiss={() => setMembersPopoverOpen(false)}
      >
        <IonList className="encode-popover">
          <IonItem lines="none">
            <div className="encode-popover-center">
              <IonText>
                <strong>{t('chat.members.title')}</strong>
              </IonText>
            </div>
          </IonItem>
          {membersLoading && (
            <IonItem lines="none">
              <div className="encode-popover-center">
                <IonSpinner name="crescent" />
              </div>
            </IonItem>
          )}
          {!membersLoading && members.length === 0 && (
            <IonItem lines="none">
              <div className="encode-popover-center">
                <IonText color="medium">
                  <small>{t('chat.members.empty')}</small>
                </IonText>
              </div>
            </IonItem>
          )}
          {!membersLoading &&
            members.map((m) => (
              <IonItem key={m.userId}>
                <IonText>
                  <strong>{getMemberLabel(m)}</strong>
                  <br />
                  <small>{t('chat.members.role', { role: m.role })}</small>
                  <br />
                  <small>{m.user.email}</small>
                </IonText>
              </IonItem>
            ))}
        </IonList>
      </IonPopover>
      <IonAlert
        isOpen={!!dismissAvailabilityTarget}
        header={t('chat.activity.dismissTitle')}
        message={t('chat.activity.dismissMessage')}
        buttons={[
          {
            text: t('common.actions.cancel'),
            role: 'cancel',
            handler: () => setDismissAvailabilityTarget(null),
          },
          {
            text: t('chat.activity.dismissConfirm'),
            handler: () => {
              if (dismissAvailabilityTarget) {
                confirmDismissAvailability(dismissAvailabilityTarget);
              }
            },
          },
        ]}
        onDidDismiss={() => setDismissAvailabilityTarget(null)}
      />
      <IonToast
        isOpen={!!toastMessage}
        message={toastMessage || ''}
        duration={1400}
        onDidDismiss={() => setToastMessage(null)}
      />
      <ShareLinkSheet
        isOpen={showShareSheet}
        url={buildChatDeepLink(conversationId)}
        shareText={buildDeepLinkShareText(
          'chat',
          buildChatDeepLink(conversationId),
          conversationName.trim() || undefined
        )}
        header={t('deepLink.chat.shareTitle')}
        subHeader={t('deepLink.chat.shareBody')}
        onDidDismiss={() => setShowShareSheet(false)}
        onCopied={() => setToastMessage(t('shareLink.copied'))}
      />
    </IonPage>
  );
};

export default ChatPage;

